import { useCallback, useEffect, useState } from 'react';
import {
  type Customer,
  type LoginResult,
  type RegisterResult,
  customerContactFromProfile,
  fetchProfile,
  getStoredToken,
  loginCustomer,
  registerCustomer,
  saveToken,
} from '../lib/customerAuth';

export function useCustomerAuth() {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = getStoredToken();
    if (!stored) {
      setIsLoading(false);
      return;
    }
    setToken(stored);
    fetchProfile(stored)
      .then((profile) => {
        if (profile) {
          setCustomer(profile);
        } else {
          saveToken(null);
          setToken(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const result = await loginCustomer(email, password);
    if (result.success && result.customer && result.token) {
      setCustomer(result.customer);
      setToken(result.token);
    }
    return result;
  }, []);

  const register = useCallback(
    async (data: {
      email: string;
      password: string;
      firstName?: string;
      lastName?: string;
    }): Promise<RegisterResult> => registerCustomer(data),
    [],
  );

  const logout = useCallback(() => {
    saveToken(null);
    setToken(null);
    setCustomer(null);
  }, []);

  return {
    customer,
    token,
    isLoading,
    isLoggedIn: !!customer,
    login,
    register,
    logout,
    contactFromProfile: customer ? customerContactFromProfile(customer) : null,
  };
}
